import { useCallback, useEffect, useState } from 'react';

import { api } from './api';

// The one way a screen gets data. A screen that watches the world change
// uses usePolled; a drawer that opens one record uses useLoad. There is no
// third mechanism, and there is one polling cadence.
export const POLL_MS = 15_000;

interface Loaded<T> {
  data: T | null;
  error: string;
  reload: () => Promise<void>;
}

// A polled query keeps its last good answer through a blip — the offline
// rule is "last synced state, never blank" — because the key never changes.
export function usePolled<T>(path: string): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(
    () =>
      api<T>('GET', path)
        .then((r) => {
          setData(r);
          setError('');
        })
        .catch((e) => setError((e as Error).message)),
    [path],
  );
  useEffect(() => {
    load();
    const t = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);
  return { data, error, reload: load };
}

// A keyed read that follows nextCursor to accumulate EVERY page — the earnings
// and reports reconciliation lists and their CSV statements must show every
// booking, not the first page (canon S6 "reconcile every euro"). Re-runs when
// the path changes; a stale in-flight walk is abandoned. Pass null to hold off.
export function useAllPages<T>(path: string | null): { items: T[]; error: string; loading: boolean } {
  const [items, setItems] = useState<T[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!path) {
      setItems([]);
      setError('');
      return;
    }
    let cancelled = false;
    setItems([]);
    setError('');
    setLoading(true);
    (async () => {
      const acc: T[] = [];
      let cursor: string | null = null;
      try {
        do {
          const sep = path.includes('?') ? '&' : '?';
          const url: string = cursor ? `${path}${sep}cursor=${encodeURIComponent(cursor)}` : path;
          const r = await api<{ items: T[]; nextCursor: string | null }>('GET', url);
          acc.push(...r.items);
          cursor = r.nextCursor;
        } while (cursor && !cancelled);
        if (!cancelled) setItems(acc);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path]);
  return { items, error, loading };
}

// A keyed one-shot fetch, re-run when the path changes. Pass null to hold
// off — hooks cannot be conditional, but a screen's inputs can be. Unlike
// usePolled, data is cleared when the key changes or the fetch fails:
// showing one record's data under another record's key is never acceptable.
export function useLoad<T>(path: string | null): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    if (!path) return Promise.resolve();
    return api<T>('GET', path)
      .then((r) => {
        setData(r);
        setError('');
      })
      .catch((e) => {
        setData(null);
        setError((e as Error).message);
      });
  }, [path]);
  useEffect(() => {
    setData(null);
    setError('');
    load();
  }, [load]);
  return { data, error, reload: load };
}
